import { FC } from "react";
import { Box, Grid } from "@mui/material";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { SitesModelType, SitesSourceModelType } from "../../../../../common/modelTypes";
import { col6 } from "../../../../../common/utils";
import PieChartDistributionType, { DataChart } from "../../../../../components/charts/graph/PieChartDistributionType";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../../../components/common/AmbientalKpiWidget";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import TableData from "../../../../../components/common/table/TableData";
import {
	DonutChartDTO,
	DonutChartItemDTO,
	EnvironmentalKpiDTO,
} from "../../../../../datasources/api-client/data-contracts";
import { useChartColors } from "../../../../../theme/theme";
import { dataColSitesSource, getColumnsSitesSource, getRowsSitesSource } from "../Greenrouter/data";

type CalculationMethods = {
	data: DonutChartDTO | undefined;
	isLoading: boolean;
	reload: VoidFunction;
};

type EnvironmentalKpi = {
	data: EnvironmentalKpiDTO | undefined;
	isLoading: boolean;
	reload: VoidFunction;
};

interface Props {
	emissionsSites: SitesModelType;
	calculationMethods?: CalculationMethods;
	nodesCertification?: CalculationMethods;
	environmentalKpi?: EnvironmentalKpi;
	siteSource: SitesSourceModelType;
}

const ThirdParty: FC<Props> = ({
	emissionsSites,
	calculationMethods,
	nodesCertification,
	environmentalKpi,
	siteSource,
}) => {
	const { t } = useTranslation("feature.units");
	const { chartColors } = useChartColors();

	// Color palette matching data order
	// The order is IMPORTANT
	const colors = [
		chartColors.method1,
		chartColors.method2,
		chartColors.method3,
		chartColors.method4,
		chartColors.method5,
		chartColors.method6,
	];

	const getDataChart = (data: DonutChartItemDTO[], t: TFunction): DataChart[] => {
		return (
			data.map((value, index) => {
				return {
					id: index,
					percent: value.percentage ? Number(value.percentage.toFixed(2)) : 0,
					value: value.value ?? 0,
					label: value.label || "",
					icon: <Box borderRadius={0.8} width={12} height={12} sx={{ backgroundColor: colors[index] }} />,
				};
			}) || []
		);
	};

	const marketBase: MarketBaseType = {
		emissions: emissionsSites.data?.thirdParty?.current.marketBasedWtw,
		delta: emissionsSites.data?.thirdParty?.current.marketBasedWtwDelta,
		coverage: emissionsSites.data?.thirdParty?.current.marketBasedWtwCoverage,
		isLoading: emissionsSites.isLoading,
		onReload: emissionsSites.reload,
	};

	return (
		<Grid container spacing={2}>
			<Grid size={col6} minHeight={150}>
				<NumericValueWidget
					title={t("overview/total emissions")}
					value={emissionsSites.data?.thirdParty?.current.co2Wtw.value}
					delta={emissionsSites.data?.thirdParty?.delta.co2Wtw}
					measure={emissionsSites.data?.thirdParty?.current.co2Wtw.measure}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
				/>
			</Grid>
			<Grid size={col6} minHeight={150}>
				<NumericValueWidget
					title={t("overview/no operational units")}
					value={emissionsSites.data?.thirdParty?.current.count}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
				/>
			</Grid>
			<Grid size={col6} minHeight={320}>
				<PieChartDistributionType
					totalEmissions={calculationMethods?.data?.total.value}
					keyEmissions={calculationMethods?.data?.total.measure}
					title={t("thirdParty/methodology used")}
					isLoading={calculationMethods?.isLoading || false}
					onReload={calculationMethods?.reload}
					dataChart={calculationMethods?.data?.items && getDataChart(calculationMethods?.data?.items, t)}
					colors={colors}
				/>
			</Grid>
			<Grid size={col6} minHeight={320}>
				<PieChartDistributionType
					totalEmissions={nodesCertification?.data?.total.value}
					keyEmissions={nodesCertification?.data?.total.measure}
					title={t("thirdParty/certification presence")}
					isLoading={nodesCertification?.isLoading || false}
					onReload={nodesCertification?.reload}
					dataChart={nodesCertification?.data?.items && getDataChart(nodesCertification?.data?.items, t)}
					colors={colors}
				/>
			</Grid>
			<Grid size={col6} minHeight={150}>
				<NumericValueWidget title={t("direct GHG gas breakdown")} measure="TBD" />
			</Grid>
			<Grid size={col6} minHeight={400}>
				<TableData
					columns={getColumnsSitesSource(dataColSitesSource, t)}
					rows={siteSource.data ? getRowsSitesSource(siteSource.data, t) : []}
					title={t("greenrouter/emissions and consumption by source")}
					isLoading={siteSource.isLoading}
					onReload={siteSource.reload}
				/>
			</Grid>

			<AmbientalKpiWidget
				bio={emissionsSites.data?.thirdParty.current.biogenic}
				bioCoverage={emissionsSites.data?.thirdParty.current.biogenicCoverage}
				loading={emissionsSites.isLoading}
				onReload={emissionsSites.reload}
			/>

			<MarketBaseWidget marketBase={marketBase} />
		</Grid>
	);
};

export default ThirdParty;
